/**
 * Analysis Hook
 * Manages analysis workflow and state with SSE for real-time progress
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  analysisApi, 
  crawlJobsApi,
  blogApi,
  schemaApi,
  type AnalysisQueue,
  type AnalysisResult,
  type BlogTag,
  type LinkRecommendationStats,
  type QueueEntry,
  type CrawlJobStatus,
  type HtmlPostCategory,
  type HtmlPostSelection,
} from '../lib/dashboard-api';
import api, { sitesApi } from '../lib/api';

// Blogs with more crawled posts than this get a one-time "select posts to
// analyze" gate before phase 2 (BKA upload) starts. Mirrors
// crawl-jobs.js POST_SELECTION_THRESHOLD on the backend.
export const POST_SELECTION_THRESHOLD = 100;

/**
 * Pause reason info when analysis is paused or stuck
 * Handles billing errors, throttling, API overload, stuck analyses, and other issues
 */
interface PauseReasonInfo {
  type: 'billing' | 'throttling' | 'overload' | 'stuck' | 'unknown';
  detected: boolean;
  message: string;
  details?: string;
  actionUrl?: string;
  canResume: boolean;
}

export interface CrawlPhase {
  jobId: string;
  status: 'queued' | 'crawling' | 'extracting' | 'done' | 'error';
  postsFound: number;
  postsExtracted: number;
  error: string | null;
}

/**
 * Blocks phase 2 (BKA upload) until the user confirms which posts to
 * analyze. Shown once per site the first time a crawl finds more than
 * POST_SELECTION_THRESHOLD posts; after that the saved selection is applied
 * automatically (see htmlPostSelectionSummary for the always-available,
 * non-blocking "Edit Selection" affordance).
 */
export interface PostSelectionGate {
  crawlJobId: string;
  isFullBaseline: boolean;
  totalPosts: number;
  categories: HtmlPostCategory[];
  savedSelection: HtmlPostSelection;
}

/** Informational (non-blocking) summary of the latest completed crawl, used to
 * render/edit the current post-selection settings any time — not just when
 * the gate above is active. */
export interface HtmlPostSelectionSummary {
  totalPosts: number;
  categories: HtmlPostCategory[];
  savedSelection: HtmlPostSelection;
}

interface AnalysisState {
  isLoading: boolean;
  error: string | null;
  /** Non-error informational message (e.g. no content changes detected). */
  infoMessage: string | null;
  // Tags
  tags: BlogTag[];
  tagsLoading: boolean;
  selectedTagIds: string[];
  totalPosts: number;
  // Active analysis
  activeAnalysis: AnalysisQueue | null;
  isAnalyzing: boolean;
  // Async crawl phase (HTML clients only — before BKA analysis begins)
  crawlPhase: CrawlPhase | null;
  // HTML clients only — set when a completed crawl needs the user to pick
  // which posts to analyze before phase 2 can proceed.
  postSelectionGate: PostSelectionGate | null;
  // HTML clients only — informational snapshot of the latest crawl's
  // categories + the site's saved selection, kept fresh so the Dashboard can
  // always offer an "Edit Selection" control, not just during the gate above.
  htmlPostSelectionSummary: HtmlPostSelectionSummary | null;
  // Paused state (e.g., billing error, throttling, API overload)
  isPaused: boolean;
  pauseReason: PauseReasonInfo | null;
  isResuming: boolean;
  // Results
  latestResults: AnalysisResult | null;
  linkStats: LinkRecommendationStats | null;
  hasResults: boolean;
  // Queue and History
  queueEntries: QueueEntry[];
  analysisHistory: AnalysisResult[];
}

const NO_CHANGES_MESSAGE =
  'No posts have changed since the last analysis. Use Full Baseline Analysis to re-analyze all posts.';

// BKA API base URL (should match backend config)
const BKA_API_BASE = import.meta.env.VITE_BKA_API_URL || 'https://dev-api.vizzex.com/api/v1';
const BATCH_SIZE = 50;

// Maps BKA step IDs to overall progress percentage ranges [start, end]
// Matches WP plugin reference implementation exactly
const STEP_RANGES: Record<string, [number, number]> = {
  step1:  [0,  10],   // uploads
  step2a: [10, 20],   // category splitting
  step2:  [20, 30],   // CLSC analysis
  step3:  [30, 40],   // cross-category bridge
  step3a: [40, 50],   // semantic relationships
  step4a: [50, 60],   // intra-category links
  step4b: [60, 70],   // inter-category links
  step5:  [70, 80],   // link filtering
  step6:  [80, 90],   // category strategy
  step7:  [90, 100],  // executive summary
};

// Maps BKA step IDs to human-readable names for progress display
const STEP_NAMES: Record<string, string> = {
  step1:  'Uploading Content',
  step2a: 'Splitting Categories',
  step2:  'CLSC Analysis',
  step3:  'Cross-Category Bridge Analysis',
  step3a: 'Semantic Relationship Mapping',
  step4a: 'Intra-Category Link Planning',
  step4b: 'Inter-Category Link Planning',
  step5:  'Link Filtering',
  step6:  'Category Strategy',
  step7:  'Executive Summary',
};

/**
 * Parse error type or message to determine pause reason
 * Handles billing errors, throttling, API overload, and other issues
 * 
 * @param errorTypeOrMessage - Either the errorType field (e.g., "BILLING_ERROR") 
 *                             or errorMessage field from BKA server
 */
function parsePauseReason(errorTypeOrMessage: string | undefined): PauseReasonInfo {
  // Default unknown reason - still resumable
  const unknownReason: PauseReasonInfo = {
    type: 'unknown',
    detected: true,
    message: 'Analysis has been paused.',
    details: errorTypeOrMessage || 'The analysis was paused. You can try resuming it.',
    canResume: true
  };

  if (!errorTypeOrMessage) {
    return unknownReason;
  }

  const msg = errorTypeOrMessage.toLowerCase();
  
  // Billing / Credit errors
  // Matches: "BILLING_ERROR", "billing_error", "credit", "insufficient_quota", etc.
  if (msg.includes('billing') || msg.includes('credit') || msg.includes('insufficient_quota') || msg.includes('payment')) {
    return {
      type: 'billing',
      detected: true,
      message: 'Your Anthropic API credits are depleted.',
      details: errorTypeOrMessage,
      actionUrl: 'https://console.anthropic.com',
      canResume: true
    };
  }
  
  // Rate limiting / Throttling
  // Matches: "RATE_LIMIT_ERROR", "rate_limit", "throttling", "429", etc.
  if (msg.includes('rate_limit') || msg.includes('rate limit') || msg.includes('throttl') || msg.includes('429') || msg.includes('too_many_requests')) {
    return {
      type: 'throttling',
      detected: true,
      message: 'Your API rate limit has been reached.',
      details: 'Your API is temporarily rate-limited. Please wait a moment before resuming.',
      canResume: true
    };
  }
  
  // API Overload / Server busy
  // Matches: "OVERLOAD_ERROR", "overloaded", "busy", "503", etc.
  if (msg.includes('overload') || msg.includes('busy') || msg.includes('503') || msg.includes('capacity') || msg.includes('unavailable')) {
    return {
      type: 'overload',
      detected: true,
      message: 'Claude API is temporarily overloaded.',
      details: 'The AI service is experiencing high demand. Please wait a moment before resuming.',
      canResume: true
    };
  }
  
  // Stuck / Stalled analysis (server-detected)
  // Matches: "STUCK", "stuck", "stall", "stalled", "no_activity", etc.
  if (msg.includes('stuck') || msg.includes('stall') || msg.includes('no_activity') || msg.includes('timeout')) {
    return {
      type: 'stuck',
      detected: true,
      message: 'Analysis appears to be stalled.',
      details: 'The analysis has not progressed for an extended period. Click Resume to continue.',
      canResume: true
    };
  }
  
  // Return with the actual error message as details
  return {
    ...unknownReason,
    details: errorTypeOrMessage
  };
}

export function useAnalysis(siteId: number | null, portalId?: string, cmsType?: string) {
  const [state, setState] = useState<AnalysisState>({
    isLoading: true,
    error: null,
    infoMessage: null,
    tags: [],
    tagsLoading: false,
    selectedTagIds: [],
    totalPosts: 0,
    activeAnalysis: null,
    isAnalyzing: false,
    crawlPhase: null,
    postSelectionGate: null,
    htmlPostSelectionSummary: null,
    isPaused: false,
    pauseReason: null,
    isResuming: false,
    latestResults: null,
    linkStats: null,
    hasResults: false,
    queueEntries: [],
    analysisHistory: [],
  });

  // SSE connection ref
  const eventSourceRef = useRef<EventSource | null>(null);
  
  // Credentials for SSE (fetched from site settings)
  const credentialsRef = useRef<{
    licenseKey: string;
    domain: string;
    instanceKey: string;
    claudeApiKey: string;
  } | null>(null);

  /**
   * Close SSE connection
   */
  const closeSSE = useCallback(() => {
    if (eventSourceRef.current) {
      console.log('Closing SSE connection');
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  /**
   * Connect to BKA SSE for analysis progress
   * Following the reference implementation (vizzex-pro) pattern
   */
  const connectToSSE = useCallback((sessionId: string) => {
    if (!credentialsRef.current) {
      console.error('No credentials available for SSE connection');
      return;
    }

    // Close existing connection if any
    closeSSE();

    const { licenseKey, domain, instanceKey } = credentialsRef.current;

    // Build SSE URL with authentication (matching reference implementation exactly)
    // Reference uses: ${vzxConfig.apiBaseUrl}/processes/baseline_analysis/sessions/${sessionId}/stream?${params}
    const params = new URLSearchParams({
      licenseKey,
      domain,
      instanceKey,
    }).toString();
    
    const sseUrl = `${BKA_API_BASE}/processes/baseline_analysis/sessions/${sessionId}/stream?${params}`;

    console.log('🔵 SSE: Connecting to analysis stream...');
    console.log('🔵 SSE URL:', sseUrl);

    // Track progress to prevent backwards movement
    let lastProgress = 50; // Start at 50% since uploads are complete

    try {
      const eventSource = new EventSource(sseUrl);
      eventSourceRef.current = eventSource;

      // Connection opened
      eventSource.addEventListener('open', () => {
        console.log('🟢 SSE: Successfully connected to analysis stream');
      });

      // Helper function to handle progress updates (used by multiple event types)
      const handleProgressUpdate = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          console.log(`📩 SSE Progress (${event.type}):`, data);

          // Capture granular detail string if provided (e.g. "Analyzing: Content Marketing (3/8)")
          const progressDetails: string | undefined = data.details || undefined;

          // Calculate overall progress
          let overallProgress: number | null = null;
          
          if (data.progressPercentage !== undefined && data.progressPercentage !== null) {
            // BKA provides overall progress directly — primary source
            overallProgress = data.progressPercentage;
            console.log(`📊 Using BKA overall progress: ${overallProgress}%`);
          } else if (data.stepProgressPercentage !== undefined && data.stepProgressPercentage !== null) {
            // Fallback: map step-level progress through the step range table
            const stepProgress = data.stepProgressPercentage as number;
            const stepId = data.currentStep as string | undefined;
            const range = stepId ? STEP_RANGES[stepId] : undefined;
            if (range) {
              const [rangeStart, rangeEnd] = range;
              overallProgress = rangeStart + (stepProgress / 100) * (rangeEnd - rangeStart);
              console.log(`📊 Step fallback (${stepId}): ${stepProgress}% → ${overallProgress.toFixed(1)}% overall`);
            } else {
              // Unknown step — linear fallback (50-100% for post-upload phase)
              overallProgress = 50 + (stepProgress * 0.5);
              console.log(`📊 Unknown step fallback: ${stepProgress}% → ${overallProgress}%`);
            }
          }

          // Safety: never reach 100% until the explicit `completed` event arrives
          if (overallProgress !== null) {
            overallProgress = Math.min(overallProgress, 99.9);
          }

          // Update state if we have progress (never move backward)
          if (overallProgress !== null && overallProgress >= lastProgress) {
            lastProgress = overallProgress;
            const stepId = data.currentStep as string | undefined;
            const stepName = stepId ? STEP_NAMES[stepId] : undefined;
            // Prefer our human-readable step name over BKA's raw internal message (e.g. "Current state: step7 (95%)")
            const message = (stepName ? `Analyzing: ${stepName}` : null) || data.message || 'Analyzing content...';
            
            setState(prev => ({
              ...prev,
              activeAnalysis: prev.activeAnalysis ? {
                ...prev.activeAnalysis,
                status: 'analyzing',
                progress: Math.round(overallProgress as number),
                progressMessage: message,
                progressDetails,
              } : null,
            }));
          } else if (data.message || progressDetails) {
            // No progress movement, but there may be a new message or detail to show
            setState(prev => ({
              ...prev,
              activeAnalysis: prev.activeAnalysis ? {
                ...prev.activeAnalysis,
                ...(data.message ? { progressMessage: data.message } : {}),
                ...(progressDetails !== undefined ? { progressDetails } : {}),
              } : null,
            }));
          }
        } catch (err) {
          console.error('Error parsing progress event:', err);
        }
      };

      // Listen for all possible progress event types from BKA server (matching reference)
      eventSource.addEventListener('subStepProgress', handleProgressUpdate);
      eventSource.addEventListener('progress', handleProgressUpdate);
      
      // Step transition events
      eventSource.addEventListener('step_started', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          const stepId = data.stepId as string | undefined;
          const humanName = (stepId && STEP_NAMES[stepId]) || data.stepName || stepId || 'Unknown step';
          console.log(`🚀 Step Started: ${humanName} (${stepId})`);
          setState(prev => ({
            ...prev,
            activeAnalysis: prev.activeAnalysis ? {
              ...prev.activeAnalysis,
              progressMessage: `Starting: ${humanName}`,
              progressDetails: undefined,
            } : null,
          }));
        } catch (err) {
          console.error('Error parsing step_started event:', err);
        }
      });

      eventSource.addEventListener('step_completed', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          const stepId = data.stepId as string | undefined;
          const humanName = (stepId && STEP_NAMES[stepId]) || data.stepName || stepId || 'Unknown step';
          console.log(`✅ Step Completed: ${humanName} (${stepId})`);
          setState(prev => ({
            ...prev,
            activeAnalysis: prev.activeAnalysis ? {
              ...prev.activeAnalysis,
              progressMessage: `Completed: ${humanName}`,
              progressDetails: undefined,
            } : null,
          }));
        } catch (err) {
          console.error('Error parsing step_completed event:', err);
        }
      });

      // Step failed event - when a step fails (may lead to pause or failure)
      eventSource.addEventListener('step_failed', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          console.error(`❌ Step Failed: ${data.stepName} (${data.stepId})`, data);
          setState(prev => ({
            ...prev,
            activeAnalysis: prev.activeAnalysis ? {
              ...prev.activeAnalysis,
              progressMessage: `⚠️ Step failed: ${data.stepName || 'Unknown step'}`,
            } : null,
          }));
        } catch (err) {
          console.error('Error parsing step_failed event:', err);
        }
      });

      // Resumed event - confirmation that analysis has resumed after pause
      eventSource.addEventListener('resumed', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          console.log('▶️ SSE: Analysis resumed', data);
          // State should already be updated by resumeAnalysis(), but update for confirmation
          setState(prev => ({
            ...prev,
            isAnalyzing: true,
            isPaused: false,
            pauseReason: null,
            activeAnalysis: prev.activeAnalysis ? {
              ...prev.activeAnalysis,
              status: 'analyzing',
              progressMessage: 'Analysis resumed...',
              errorMessage: undefined,
            } : null,
          }));
        } catch (err) {
          console.error('Error parsing resumed event:', err);
        }
      });

      // Session state changes
      eventSource.addEventListener('session_updated', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📋 Session Updated:', data);

          // BKA sends newStatus (not overallStatus) and errorType as separate field
          const status = data.newStatus || data.overallStatus || data.status;
          const errorType = data.errorType;  // e.g., "BILLING_ERROR"
          const errorMessage = data.errorMessage || data.message;

          if (status === 'completed') {
            console.log('✅ SSE: Analysis completed via session_updated');
            closeSSE();
            handleAnalysisComplete(sessionId);
          } else if (status === 'failed' || status === 'error') {
            console.error('❌ SSE: Analysis failed via session_updated');
            closeSSE();
            setState(prev => ({
              ...prev,
              isAnalyzing: false,
              isPaused: false,
              pauseReason: null,
              error: errorMessage || 'Analysis failed',
            }));
          } else if (status === 'paused') {
            // Handle paused status (e.g., billing error, throttling, overload)
            console.warn('⏸️ SSE: Analysis paused via session_updated');
            console.log('  errorType:', errorType);
            console.log('  errorMessage:', errorMessage);
            console.log('  currentStep:', data.currentStep);
            
            // Use errorType if available, otherwise parse from errorMessage
            const pauseReason = parsePauseReason(errorType || errorMessage);
            
            // Build appropriate progress message based on pause reason
            let progressMessage = 'Analysis paused';
            if (pauseReason.type === 'billing') {
              progressMessage = 'Analysis paused: API credits depleted';
            } else if (pauseReason.type === 'throttling') {
              progressMessage = 'Analysis paused: Rate limit reached';
            } else if (pauseReason.type === 'overload') {
              progressMessage = 'Analysis paused: API temporarily overloaded';
            } else if (errorMessage) {
              progressMessage = errorMessage;
            }
            
            // Sync the paused status to the backend database
            if (siteId) {
              analysisApi.syncStatus(siteId, sessionId, 'paused', errorType, errorMessage, progressMessage)
                .then(() => console.log('✓ Paused status synced to database'))
                .catch(err => console.warn('Failed to sync paused status:', err.message));
            }
            
            setState(prev => ({
              ...prev,
              isAnalyzing: false,
              isPaused: true,
              pauseReason: {
                ...pauseReason,
                details: errorMessage || pauseReason.details,  // Preserve full error message
              },
              activeAnalysis: prev.activeAnalysis ? {
                ...prev.activeAnalysis,
                status: 'paused',
                progressMessage,
                errorMessage: errorMessage,
              } : null,
            }));
            
            // Don't close SSE - we may resume
          } else if (status === 'stuck') {
            // Handle stuck status (server-detected stall)
            console.warn('⚠️ SSE: Analysis stuck via session_updated');
            closeSSE();
            
            const stuckReason: PauseReasonInfo = {
              type: 'stuck',
              detected: true,
              message: data.message || 'Analysis stalled. Click Resume to continue.',
              details: errorMessage || 'No activity detected for extended period.',
              canResume: data.resumable !== false
            };
            
            // Sync to database
            if (siteId) {
              analysisApi.syncStatus(siteId, sessionId, 'stuck', 'STUCK', errorMessage, stuckReason.message)
                .then(() => console.log('✓ Stuck status synced to database'))
                .catch(err => console.warn('Failed to sync stuck status:', err.message));
            }
            
            setState(prev => ({
              ...prev,
              isAnalyzing: false,
              isPaused: true,
              pauseReason: stuckReason,
              activeAnalysis: prev.activeAnalysis ? {
                ...prev.activeAnalysis,
                status: 'stuck',
                progressMessage: stuckReason.message,
              } : null,
            }));
          } else if (status === 'processing' || status === 'running' || status === 'analyzing') {
            handleProgressUpdate(event);
          }
        } catch (err) {
          console.error('Error parsing session_updated event:', err);
        }
      });

      // Paused event - dedicated event for pause states (billing error, rate limit, etc.)
      eventSource.addEventListener('paused', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          console.log('⏸️ SSE: Analysis paused via dedicated paused event');
          console.log('  errorType:', data.errorType);
          console.log('  errorMessage:', data.errorMessage);
          console.log('  resumeEndpoint:', data.resumeEndpoint);
          
          // Parse pause reason from errorType or errorMessage
          const pauseReason = parsePauseReason(data.errorType || data.errorMessage);
          
          // Build appropriate progress message based on error type
          let progressMessage = 'Analysis paused';
          if (data.errorType === 'BILLING_ERROR') {
            progressMessage = 'Analysis paused: API credits depleted';
          } else if (data.errorType === 'RATE_LIMIT_ERROR') {
            progressMessage = 'Analysis paused: Rate limit reached';
          } else if (data.errorType === 'OVERLOAD_ERROR') {
            progressMessage = 'Analysis paused: API temporarily overloaded';
          } else if (data.errorMessage) {
            progressMessage = data.errorMessage;
          }
          
          // Sync the paused status to the backend database
          if (siteId) {
            analysisApi.syncStatus(siteId, sessionId, 'paused', data.errorType, data.errorMessage, progressMessage)
              .then(() => console.log('✓ Paused status synced to database'))
              .catch(err => console.warn('Failed to sync paused status:', err.message));
          }
          
          setState(prev => ({
            ...prev,
            isAnalyzing: false,
            isPaused: true,
            pauseReason: {
              ...pauseReason,
              details: data.errorMessage || pauseReason.details,
            },
            activeAnalysis: prev.activeAnalysis ? {
              ...prev.activeAnalysis,
              status: 'paused',
              progressMessage,
              errorMessage: data.errorMessage,
            } : null,
          }));
          
          // Don't close SSE - we may resume
        } catch (err) {
          console.error('Error parsing paused event:', err);
        }
      });

      // Stuck event - server-detected stall (no activity for extended period)
      eventSource.addEventListener('stuck', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          console.log('⚠️ SSE: Analysis stuck (server-detected)');
          console.log('  sessionId:', data.sessionId);
          console.log('  stuckDuration:', data.stuckDuration);
          console.log('  currentStep:', data.currentStep);
          console.log('  resumable:', data.resumable);
          
          // Close SSE - analysis is stuck, no point listening
          closeSSE();
          
          // Build pause reason for stuck state
          const stuckReason: PauseReasonInfo = {
            type: 'stuck',
            detected: true,
            message: data.message || 'Analysis stalled. Click Resume to continue.',
            details: data.stuckDuration 
              ? `Stalled at ${data.currentStep || 'unknown step'} for ${Math.floor(data.stuckDuration / 60)} minutes.`
              : 'No activity detected for extended period.',
            canResume: data.resumable !== false
          };
          
          // Sync the stuck status to the backend database
          if (siteId) {
            analysisApi.syncStatus(siteId, sessionId, 'stuck', 'STUCK', data.message, stuckReason.message)
              .then(() => console.log('✓ Stuck status synced to database'))
              .catch(err => console.warn('Failed to sync stuck status:', err.message));
          }
          
          setState(prev => ({
            ...prev,
            isAnalyzing: false,
            isPaused: true, // Reuse isPaused state - stuck is similar to paused
            pauseReason: stuckReason,
            activeAnalysis: prev.activeAnalysis ? {
              ...prev.activeAnalysis,
              status: 'stuck',
              progressMessage: stuckReason.message,
            } : null,
          }));
        } catch (err) {
          console.error('Error parsing stuck event:', err);
        }
      });

      // sessionStatusChanged — WP plugin alias for session_updated; forward to progress handler
      eventSource.addEventListener('sessionStatusChanged', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📋 sessionStatusChanged (alias for session_updated):', data);
          const status = data.newStatus || data.overallStatus || data.status;
          if (status === 'completed') {
            closeSSE();
            handleAnalysisComplete(sessionId);
          } else {
            // Treat like a progress tick — details/message may carry useful info
            handleProgressUpdate(event);
          }
        } catch (err) {
          console.error('Error parsing sessionStatusChanged event:', err);
        }
      });

      // failed — direct failure event from BKA
      eventSource.addEventListener('failed', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          console.error('❌ SSE: Analysis failed via dedicated failed event', data);
          closeSSE();
          const errorMsg = data.errorMessage || data.message || data.error || 'Analysis failed';
          setState(prev => ({
            ...prev,
            isAnalyzing: false,
            isPaused: false,
            pauseReason: null,
            error: errorMsg,
            activeAnalysis: prev.activeAnalysis ? {
              ...prev.activeAnalysis,
              status: 'failed',
              progressMessage: errorMsg,
              progressDetails: undefined,
            } : null,
          }));
        } catch (err) {
          console.error('Error parsing failed event:', err);
        }
      });

      // resumable — BKA signals the session can be resumed
      eventSource.addEventListener('resumable', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          console.log('🔄 SSE: Analysis is resumable', data);
          // Treat as paused with unknown reason so the Resume button appears
          const pauseReason = parsePauseReason(data.errorType || data.message);
          setState(prev => ({
            ...prev,
            isAnalyzing: false,
            isPaused: true,
            pauseReason: {
              ...pauseReason,
              canResume: true,
            },
            activeAnalysis: prev.activeAnalysis ? {
              ...prev.activeAnalysis,
              status: 'paused',
              progressMessage: data.message || 'Analysis paused — ready to resume.',
            } : null,
          }));
        } catch (err) {
          console.error('Error parsing resumable event:', err);
        }
      });

      // cancelled — analysis was cancelled (by user or server)
      eventSource.addEventListener('cancelled', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          console.log('🚫 SSE: Analysis cancelled', data);
          closeSSE();
          setState(prev => ({
            ...prev,
            isAnalyzing: false,
            isPaused: false,
            pauseReason: null,
            activeAnalysis: prev.activeAnalysis ? {
              ...prev.activeAnalysis,
              status: 'cancelled',
              progressMessage: data.message || 'Analysis cancelled.',
              progressDetails: undefined,
            } : null,
          }));
        } catch (err) {
          console.error('Error parsing cancelled event:', err);
        }
      });

      // Completion event — only here do we allow progress to reach 100%
      eventSource.addEventListener('completed', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          console.log('✅ SSE: Analysis completed!', data);
          closeSSE();
          
          setState(prev => ({
            ...prev,
            activeAnalysis: prev.activeAnalysis ? {
              ...prev.activeAnalysis,
              status: 'completed',
              progress: 100,
              progressMessage: 'Analysis complete! Fetching results...',
              progressDetails: undefined,
            } : null,
          }));
          
          handleAnalysisComplete(sessionId);
        } catch (err) {
          console.error('Error parsing completed event:', err);
          // Still try to fetch results
          handleAnalysisComplete(sessionId);
        }
      });

      // Error event (from BKA, not connection error)
      eventSource.addEventListener('error', (event: MessageEvent) => {
        // Check if this is a BKA error event (has data) or connection error (no data)
        if (event.data) {
          try {
            const data = JSON.parse(event.data);
            console.error('❌ SSE: Analysis error:', data);
            closeSSE();
            setState(prev => ({
              ...prev,
              isAnalyzing: false,
              error: data.message || data.error || 'Analysis failed',
            }));
          } catch (err) {
            console.error('Error parsing error event:', err);
          }
        }
      });

      // Generic message handler (fallback)
      eventSource.onmessage = (event) => {
        console.log('📩 SSE generic message:', event.data);
        handleProgressUpdate(event);
      };

      // Connection error handler
      eventSource.onerror = (event) => {
        console.error('🔴 SSE Connection Error:', event);
        
        if (eventSource.readyState === EventSource.CLOSED) {
          console.log('SSE connection closed');
          // Don't set error - might have completed normally
        } else if (eventSource.readyState === EventSource.CONNECTING) {
          console.log('SSE reconnecting...');
        }
      };

    } catch (err) {
      console.error('Failed to create EventSource:', err);
      setState(prev => ({
        ...prev,
        error: 'Failed to connect to analysis server',
      }));
    }
  }, [closeSSE]);

  /**
   * Fetch credentials for SSE connection from dedicated endpoint
   */
  const fetchCredentials = useCallback(async () => {
    if (!siteId) return null;

    try {
      // Fetch analysis credentials (full credentials for SSE)
      const { data } = await api.get(`/api/sites/${siteId}/analysis-credentials`);

      if (data.success) {
        credentialsRef.current = {
          licenseKey: data.licenseKey || '',
          domain: data.domain || '',
          instanceKey: data.instanceKey || '',
          claudeApiKey: data.claudeApiKey || '',
        };
        return credentialsRef.current;
      }
    } catch (err) {
      console.error('Failed to fetch credentials:', err);
    }
    return null;
  }, [siteId]);

  /**
   * Handle analysis completion - fetch results from BKA and store, then refresh data
   */
  const handleAnalysisComplete = useCallback(async (sessionId: string) => {
    if (!siteId) return;

    console.log('📥 handleAnalysisComplete called for session:', sessionId);

    try {
      // Step 1: Call fetchResults to fetch from BKA and store in our database
      const fetchResponse = await analysisApi.fetchResults(siteId, sessionId);
      console.log('📥 fetchResults response:', fetchResponse);

      if (!fetchResponse.success) {
        throw new Error(fetchResponse.error || 'Failed to fetch and store results');
      }

      console.log('✅ Results stored, analysisId:', fetchResponse.analysisId);

      // Step 2: Now fetch the stored results from our database
      const latestResponse = await analysisApi.getLatest(siteId);
      console.log('📥 getLatest response:', latestResponse);

      // Step 3: Also refresh queue and history
      const [queueResponse, historyResponse] = await Promise.all([
        analysisApi.getQueue(siteId).catch(() => ({ success: false, entries: [], total: 0 })),
        analysisApi.getHistory(siteId).catch(() => ({ success: false, analyses: [] })),
      ]);

      // Backend returns 'analysis' not 'results'
      const results = latestResponse.hasResults && 'analysis' in latestResponse
        ? (latestResponse as { analysis: AnalysisResult }).analysis || null
        : null;

      clearCrawlJobId();

      setState(prev => ({
        ...prev,
        isAnalyzing: false,
        activeAnalysis: null,
        crawlPhase: null,
        latestResults: results,
        hasResults: latestResponse.hasResults,
        queueEntries: queueResponse.success ? queueResponse.entries : [],
        analysisHistory: historyResponse.success ? historyResponse.analyses : [],
      }));

      console.log('✅ Dashboard refreshed with new results:', results);

      // Auto-inject post schemas if schema output is enabled for this site.
      // Fire-and-forget: does not affect analysis UI or error state.
      if (siteId) {
        schemaApi.getGlobalSettings(siteId)
          .then(settings => {
            if (settings.schemaOutputEnabled) {
              return schemaApi.fetchAndInjectPosts(siteId)
                .then(result => {
                  if (result.skipped) return;
                  console.log(`[Schema] Auto-inject after analysis: injected=${result.injected ?? 0} failed=${result.failed ?? 0} unchanged=${result.unchanged ?? 0}`);
                });
            }
            if (settings.enableFaqExtraction) {
              // Schema output/injection is off (e.g. HTML/no-CMS clients, which have no
              // injection target and never enable it), but FAQ extraction is still on —
              // pull the freshly-generated FAQs into faq_data directly so they show up
              // on the Post FAQs / FAQ Review tab without waiting for a manual "Fetch FAQs" click.
              return schemaApi.fetchFaqsFromBka(siteId)
                .then(result => {
                  console.log(`[FAQs] Auto-fetch after analysis: stored=${result.totalStored} posts=${result.postCount}`);
                });
            }
          })
          .catch(err => {
            console.warn('[Schema] Auto-inject/FAQ-fetch after analysis failed (non-fatal):', err.message);
          });
      }

    } catch (err) {
      console.error('❌ Failed to fetch results:', err);
      setState(prev => ({
        ...prev,
        isAnalyzing: false,
        error: err instanceof Error ? err.message : 'Failed to fetch analysis results',
      }));
    }
  }, [siteId]);

  // ── localStorage key for the in-flight crawl job ───────────────────────────
  const crawlJobStorageKey = siteId ? `vizzex_crawl_job_${siteId}` : null;

  const saveCrawlJobId = useCallback((jobId: string) => {
    if (crawlJobStorageKey) localStorage.setItem(crawlJobStorageKey, jobId);
  }, [crawlJobStorageKey]);

  const clearCrawlJobId = useCallback(() => {
    if (crawlJobStorageKey) localStorage.removeItem(crawlJobStorageKey);
  }, [crawlJobStorageKey]);

  // ── Crawl polling (reusable — called from startAnalysis AND fetchInitialData) ──

  /**
   * Poll a crawl job until done or error, updating crawlPhase state throughout.
   * Returns the completed job status (for phase-2 handoff).
   * Throws on error status.
   */
  const pollCrawlJob = useCallback((jobId: string): Promise<CrawlJobStatus> => {
    return new Promise<CrawlJobStatus>((resolve, reject) => {
      const pollInterval = setInterval(async () => {
        try {
          const jobStatus = await crawlJobsApi.getStatus(jobId);
          console.log(`🕷️ [Crawl] ${jobId}: ${jobStatus.status} (${jobStatus.postsExtracted}/${jobStatus.postsFound})`);

          setState(prev => ({
            ...prev,
            crawlPhase: {
              jobId,
              status: jobStatus.status,
              postsFound: jobStatus.postsFound,
              postsExtracted: jobStatus.postsExtracted,
              error: jobStatus.error,
            },
          }));

          if (jobStatus.status === 'done') {
            clearInterval(pollInterval);
            resolve(jobStatus);
          } else if (jobStatus.status === 'error') {
            clearInterval(pollInterval);
            reject(new Error(`Blog crawl failed: ${jobStatus.error || 'unknown error'}`));
          }
        } catch (pollErr) {
          console.error('🕷️ [Crawl] Poll error (retrying):', pollErr);
          // Keep retrying on transient errors
        }
      }, 3000);
    });
  }, []);

  /**
   * Inner helper: run the BKA upload + SSE analysis flow given a valid startResponse.
   * Extracted so it can be called from both the standard path and after async crawl.
   */
  const runBkaAnalysis = useCallback(async (startResponse: {
    sessionId?: string;
    postsCount?: number;
    analysisType?: string;
    stats?: unknown;
    structuralExcluded?: number;
  }) => {
      const sessionId = startResponse.sessionId!;
      const postsCount = startResponse.postsCount || 0;
      const analysisType = startResponse.analysisType || 'hierarchical';
      const stats = startResponse.stats as { new: number; changed: number; deleted: number; unchanged: number } | undefined;
      const structuralExcluded: number = startResponse.structuralExcluded || 0;
      
      if (analysisType === 'incremental' && stats) {
        console.log(`📊 [Analysis] Incremental stats: ${stats.new} new, ${stats.changed} changed, ${stats.deleted} deleted, ${stats.unchanged} unchanged`);
      }
      
      console.log('✅ [Analysis] Session created:', { sessionId, postsCount, analysisType });

      setState(prev => ({
        ...prev,
        activeAnalysis: {
          sessionId,
          status: 'uploading',
          progress: 0,
          progressMessage: 'Preparing posts for upload...',
          postsCount,
          postsUploaded: 0,
          startedAt: new Date().toISOString(),
          structuralExcluded: structuralExcluded > 0 ? structuralExcluded : undefined,
        },
      }));

      const totalBatches = Math.max(1, Math.ceil(postsCount / BATCH_SIZE));
      console.log('📡 [Analysis] Uploading', totalBatches, 'batches...');

      for (let i = 0; i < totalBatches; i++) {
        const batchNumber = i + 1;
        const actualPostsUploaded = Math.min(batchNumber * BATCH_SIZE, postsCount);

        setState(prev => ({
          ...prev,
          activeAnalysis: prev.activeAnalysis ? {
            ...prev.activeAnalysis,
            progress: Math.round((batchNumber / totalBatches) * 50),
            progressMessage: `Uploading batch ${batchNumber} of ${totalBatches}...`,
            postsUploaded: actualPostsUploaded,
          } : null,
        }));

        const uploadResponse = await analysisApi.uploadBatch(siteId!, {
          sessionId,
          batchNumber,
          totalBatches,
        });

        if (!uploadResponse.success) {
          throw new Error(uploadResponse.error || `Failed to upload batch ${batchNumber}`);
        }
      }

      setState(prev => ({
        ...prev,
        activeAnalysis: prev.activeAnalysis ? {
          ...prev.activeAnalysis,
          status: 'analyzing',
          progress: 50,
          progressMessage: 'Analyzing content...',
        } : null,
      }));

      connectToSSE(sessionId);
      console.log('✅ [Analysis] SSE connection initiated');
  }, [siteId, connectToSSE]);

  /**
   * Start analysis
   * 
   * @param isFullBaseline - true for full re-analysis, false for incremental (only new/changed)
   */
  const startAnalysis = useCallback(async (isFullBaseline: boolean) => {
    console.log('🚀 [Analysis] Starting analysis...', { siteId, isFullBaseline, selectedTagIds: state.selectedTagIds });
    
    if (!siteId) {
      throw new Error('No site selected');
    }

    // Ensure we have credentials
    if (!credentialsRef.current) {
      console.log('🔑 [Analysis] Fetching credentials...');
      await fetchCredentials();
    }

    if (!credentialsRef.current) {
      console.error('❌ [Analysis] Missing credentials after fetch');
      throw new Error('Missing credentials for analysis');
    }
    console.log('✅ [Analysis] Credentials available');

    setState(prev => ({ ...prev, error: null, infoMessage: null, isAnalyzing: true, crawlPhase: null, postSelectionGate: null }));

    try {
      // Step 1: Initiate analysis (or crawl, for HTML clients)
      console.log('📡 [Analysis] Step 1: Calling analysisApi.start...');
      const startResponse = await analysisApi.start(siteId, {
        forceBaseline: isFullBaseline,
        selectedTagIds: state.selectedTagIds.length > 0 ? state.selectedTagIds : undefined,
      });
      console.log('📡 [Analysis] Step 1 Response:', startResponse);

      // ── HTML async crawl phase ──────────────────────────────────────────────
      if (startResponse.phase === 'crawling' && startResponse.jobId) {
        const crawlJobId = startResponse.jobId;
        console.log(`🕷️ [Analysis] Async crawl started — jobId: ${crawlJobId}`);

        saveCrawlJobId(crawlJobId);

        setState(prev => ({
          ...prev,
          crawlPhase: {
            jobId: crawlJobId,
            status: 'queued',
            postsFound: 0,
            postsExtracted: 0,
            error: null,
          },
        }));

        // Poll until crawl is complete — the resolved status carries
        // totalPosts/categories/savedSelection once status === 'done'.
        const crawlDoneStatus = await pollCrawlJob(crawlJobId);
        console.log('🕷️ [Analysis] Crawl complete');

        // Keep crawlPhase visible (as 'done') while phase 2 runs so the stepper shows both steps
        setState(prev => ({
          ...prev,
          crawlPhase: prev.crawlPhase ? { ...prev.crawlPhase, status: 'done' } : null,
        }));

        // Update the informational (non-blocking) selection summary regardless
        // of whether the gate fires, so the Dashboard can always offer "Edit
        // Selection" once we know about a completed crawl.
        if (crawlDoneStatus.totalPosts !== undefined) {
          setState(prev => ({
            ...prev,
            htmlPostSelectionSummary: {
              totalPosts: crawlDoneStatus.totalPosts!,
              categories: crawlDoneStatus.categories || [],
              savedSelection: crawlDoneStatus.savedSelection || { maxPosts: null, categories: null, configured: false },
            },
          }));
        }

        // ── Post-selection gate (migration 065) ──────────────────────────
        // First time this site's crawl finds more than the threshold and the
        // user has never confirmed a selection, pause here instead of
        // auto-continuing to phase 2.
        if (
          crawlDoneStatus.totalPosts !== undefined &&
          crawlDoneStatus.totalPosts > POST_SELECTION_THRESHOLD &&
          !crawlDoneStatus.savedSelection?.configured
        ) {
          console.log(`🗂️ [Analysis] ${crawlDoneStatus.totalPosts} posts found — gating on post selection`);
          setState(prev => ({
            ...prev,
            isAnalyzing: false,
            postSelectionGate: {
              crawlJobId,
              isFullBaseline,
              totalPosts: crawlDoneStatus.totalPosts!,
              categories: crawlDoneStatus.categories || [],
              savedSelection: crawlDoneStatus.savedSelection || { maxPosts: null, categories: null, configured: false },
            },
          }));
          return;
        }

        console.log('🕷️ [Analysis] Proceeding to BKA analysis');

        // Phase 2: hand off to BKA analysis with the completed crawl job
        const phase2Response = await analysisApi.start(siteId, {
          forceBaseline: isFullBaseline,
          selectedTagIds: state.selectedTagIds.length > 0 ? state.selectedTagIds : undefined,
          crawlJobId,
        });

        if (!phase2Response.success || !phase2Response.sessionId) {
          if (phase2Response.noChanges) {
            setState(prev => ({
              ...prev,
              isAnalyzing: false,
              crawlPhase: null,
              error: null,
              infoMessage: phase2Response.message || NO_CHANGES_MESSAGE,
            }));
            clearCrawlJobId();
            return;
          }
          if (phase2Response.alreadyRunning) {
            // Another tab/click already has an analysis running for this site —
            // don't show an error, reconnect to the real session instead so its
            // progress shows up here too.
            console.log('⏳ [Analysis] Another analysis is already running — reconnecting to it...');
            clearCrawlJobId();
            setState(prev => ({ ...prev, crawlPhase: null }));
            await fetchInitialData();
            return;
          }
          throw new Error(phase2Response.error || 'Failed to start BKA analysis after crawl');
        }

        await runBkaAnalysis(phase2Response);
        return;
      }

      // ── Standard path (HubSpot or HTML phase-2 fallthrough) ────────────────
      if (!startResponse.success || !startResponse.sessionId) {
        console.error('❌ [Analysis] Step 1 Failed:', startResponse.error);
        
        if (startResponse.noChanges) {
          setState(prev => ({
            ...prev,
            isAnalyzing: false,
            error: null,
            infoMessage: startResponse.message || NO_CHANGES_MESSAGE,
          }));
          return;
        }

        if (startResponse.alreadyRunning) {
          // Another tab/click already has an analysis running for this site —
          // don't show an error, reconnect to the real session instead so its
          // progress shows up here too.
          console.log('⏳ [Analysis] Another analysis is already running — reconnecting to it...');
          await fetchInitialData();
          return;
        }

        throw new Error(startResponse.error || 'Failed to start analysis');
      }

      await runBkaAnalysis(startResponse);

    } catch (err) {
      console.error('❌ [Analysis] Error during analysis flow:', err);
      const error = err as Error;
      closeSSE();
      clearCrawlJobId();
      setState(prev => ({
        ...prev,
        isAnalyzing: false,
        crawlPhase: null,
        activeAnalysis: null,
        infoMessage: null,
        error: error.message || 'Failed to start analysis',
      }));
      throw err;
    }
  }, [siteId, state.selectedTagIds, fetchCredentials, connectToSSE, closeSSE, pollCrawlJob, saveCrawlJobId, clearCrawlJobId]);

  /**
   * Resolves an active postSelectionGate — called once the user confirms
   * which posts to analyze (most-recent-N and/or categories). Completes
   * phase 2 for the crawl job the gate was raised for.
   *
   * @param maxPosts - null means "no cap"
   * @param selectedCategories - null/[] means "all categories"
   */
  const confirmPostSelection = useCallback(async (
    maxPosts: number | null,
    selectedCategories: string[] | null,
  ) => {
    const gate = state.postSelectionGate;
    if (!gate || !siteId) return;

    setState(prev => ({ ...prev, postSelectionGate: null, isAnalyzing: true, error: null }));

    try {
      if (!credentialsRef.current) await fetchCredentials();

      const phase2Response = await analysisApi.start(siteId, {
        forceBaseline: gate.isFullBaseline,
        crawlJobId: gate.crawlJobId,
        htmlMaxPosts: maxPosts,
        htmlSelectedCategories: selectedCategories,
      });

      // Reflect the confirmed choice in the informational summary immediately
      // (rather than waiting for the next crawl) so "Edit Selection" shows it.
      setState(prev => ({
        ...prev,
        htmlPostSelectionSummary: prev.htmlPostSelectionSummary ? {
          ...prev.htmlPostSelectionSummary,
          savedSelection: { maxPosts, categories: selectedCategories, configured: true },
        } : null,
      }));

      if (!phase2Response.success || !phase2Response.sessionId) {
        if (phase2Response.noChanges) {
          setState(prev => ({
            ...prev,
            isAnalyzing: false,
            crawlPhase: null,
            error: null,
            infoMessage: phase2Response.message || NO_CHANGES_MESSAGE,
          }));
          clearCrawlJobId();
          return;
        }
        if (phase2Response.alreadyRunning) {
          console.log('⏳ [Analysis] Another analysis is already running — reconnecting to it...');
          clearCrawlJobId();
          setState(prev => ({ ...prev, crawlPhase: null }));
          await fetchInitialData();
          return;
        }
        throw new Error(phase2Response.error || 'Failed to start BKA analysis after crawl');
      }

      await runBkaAnalysis(phase2Response);
    } catch (err) {
      console.error('❌ [Analysis] Error confirming post selection:', err);
      const error = err as Error;
      closeSSE();
      clearCrawlJobId();
      setState(prev => ({
        ...prev,
        isAnalyzing: false,
        crawlPhase: null,
        activeAnalysis: null,
        error: error.message || 'Failed to start analysis',
      }));
    }
  }, [state.postSelectionGate, siteId, fetchCredentials, runBkaAnalysis, closeSSE, clearCrawlJobId]);

  /**
   * Dismisses the post-selection gate without starting analysis (e.g. user
   * navigates away). Analysis can be restarted later, which re-raises the
   * gate since no selection was confirmed/saved.
   */
  const cancelPostSelectionGate = useCallback(() => {
    setState(prev => ({ ...prev, postSelectionGate: null, isAnalyzing: false }));
  }, []);

  /**
   * Fetch initial data (tags, active analysis, latest results)
   */
  const fetchInitialData = useCallback(async () => {
    if (!siteId) {
      setState(prev => ({ ...prev, isLoading: false }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Fetch credentials first
      await fetchCredentials();

      // Fetch tags, active analysis, latest results, queue, and history in parallel
      // HTML clients have no HubSpot tags — skip the tag fetch entirely to avoid
      // a failing HubSpot API call (their platformId is an MD5 hash, not a portal ID).
      const isHtmlSite = cmsType === 'html';
      const [tagsResponse, activeResponse, latestResponse, queueResponse, historyResponse] = await Promise.all([
        isHtmlSite
          ? Promise.resolve({ success: true, tags: [], totalPosts: 0 })
          : portalId 
            ? blogApi.getTagsWithCounts(portalId).catch((err) => {
                console.error('🏷️ Failed to fetch tags with counts:', err);
                return { success: false, tags: [], totalPosts: 0 };
              })
            : blogApi.getTags(siteId).catch((err) => {
                console.error('🏷️ Failed to fetch tags:', err);
                return { success: false, tags: [] };
              }),
        analysisApi.getActive(siteId).catch(() => ({ success: false, hasActiveAnalysis: false })),
        analysisApi.getLatest(siteId).catch(() => ({ success: false, hasResults: false })),
        analysisApi.getQueue(siteId).catch(() => ({ success: false, entries: [], total: 0 })),
        analysisApi.getHistory(siteId).catch(() => ({ success: false, analyses: [] })),
      ]);

      // Backend returns 'queue' not 'analysis' for active
      const activeAnalysis = activeResponse.hasActiveAnalysis && 'queue' in activeResponse 
        ? (activeResponse as { queue: AnalysisQueue }).queue || null 
        : null;
      // Backend returns 'analysis' not 'results' for latest
      const latestResults = latestResponse.hasResults && 'analysis' in latestResponse
        ? (latestResponse as { analysis: AnalysisResult }).analysis || null
        : null;
      // Backend returns 'linkRecommendations' not 'linkStats'
      const linkStats = 'linkRecommendations' in latestResponse 
        ? (latestResponse as { linkRecommendations: LinkRecommendationStats }).linkRecommendations || null 
        : null;
      const totalPosts = 'totalPosts' in tagsResponse ? (tagsResponse.totalPosts as number) || 0 : 0;

      // Select all tags by default
      const allTagIds = tagsResponse.success && tagsResponse.tags 
        ? tagsResponse.tags.map(t => t.id) 
        : [];

      // Extract queue entries and history
      const queueEntries = queueResponse.success ? queueResponse.entries : [];
      const analysisHistory = historyResponse.success ? historyResponse.analyses : [];

      // Determine if we should show the progress UI:
      // - Only show for IN-PROGRESS analyses (not completed ones found on page load)
      // - Completed analyses on page load should be fetched silently
      // - Paused/Stuck analyses should show the paused UI with Resume button
      const isInProgressAnalysis = activeAnalysis !== null && 
        activeAnalysis.status !== 'completed' && 
        activeAnalysis.status !== 'failed' &&
        activeAnalysis.status !== 'cancelled' &&
        activeAnalysis.status !== 'stuck'; // Stuck is handled separately with paused UI
      
      // Check if analysis is paused (billing error, throttling, overload) or stuck (server-detected stall)
      const isPausedOrStuckAnalysis = activeAnalysis?.status === 'paused' || activeAnalysis?.status === 'stuck';
      
      // Build pause reason - for stuck, create explicit stuck reason
      let pauseReason: PauseReasonInfo | null = null;
      if (isPausedOrStuckAnalysis) {
        if (activeAnalysis?.status === 'stuck') {
          pauseReason = {
            type: 'stuck',
            detected: true,
            message: 'Analysis stalled. Click Resume to continue.',
            details: activeAnalysis?.errorMessage || 'No activity detected for extended period.',
            canResume: true
          };
        } else {
          pauseReason = parsePauseReason(activeAnalysis?.errorMessage);
        }
      }

      setState(prev => ({
        ...prev,
        isLoading: false,
        tags: tagsResponse.success ? tagsResponse.tags : [],
        selectedTagIds: allTagIds, // Default: all tags selected
        totalPosts,
        // Only set activeAnalysis if it's actually in progress or paused/stuck (not completed on page load)
        activeAnalysis: isInProgressAnalysis || isPausedOrStuckAnalysis ? activeAnalysis : null,
        isAnalyzing: isInProgressAnalysis && !isPausedOrStuckAnalysis,
        isPaused: isPausedOrStuckAnalysis,
        pauseReason,
        latestResults,
        linkStats,
        hasResults: latestResponse.hasResults,
        queueEntries,
        analysisHistory,
      }));

      // Handle active analysis based on status
      if (activeAnalysis?.sessionId) {
        if (activeAnalysis.status === 'completed') {
          // Analysis completed but results not fetched yet - fetch them SILENTLY
          console.log('📥 Found completed analysis on page load, fetching results silently...');
          handleAnalysisComplete(activeAnalysis.sessionId);
        } else if (activeAnalysis.status === 'paused' || activeAnalysis.status === 'stuck') {
          console.log(`⏸️ Found ${activeAnalysis.status} analysis on page load`);
        } else if (isInProgressAnalysis) {
          // Analysis still running - connect to SSE for progress
          console.log('🔌 Reconnecting to active analysis SSE...');
          connectToSSE(activeAnalysis.sessionId);
        }
      }

      // ── HTML client: resume crawl phase if a job is in-flight ────────────────
      // Check for an active crawl job even after page reload.
      // Priority: localStorage saved jobId → else query latest from server.
      if (siteId) {
        try {
          // Check if the current latest analysis already covers the crawl job
          // (i.e. phase 2 already ran) — if so, don't re-enter crawl phase.
          const storedJobId = crawlJobStorageKey ? localStorage.getItem(crawlJobStorageKey) : null;
          const latestCrawl = await crawlJobsApi.getLatest(siteId);
          const crawlJob = storedJobId
            ? await crawlJobsApi.getStatus(storedJobId).catch(() => null)
            : latestCrawl.job;

          if (crawlJob && crawlJob.status !== 'error') {
            const isActive = ['queued', 'crawling', 'extracting'].includes(crawlJob.status);
            const isDone = crawlJob.status === 'done';

            // Don't re-enter crawl UI if an analysis is already running or results exist
            // from after this crawl job was created
            const analysisAlreadyStarted = !!activeAnalysis || (
              latestResults !== null &&
              new Date(latestResults.createdAt) > new Date(crawlJob.createdAt)
            );

            if (isActive && !analysisAlreadyStarted) {
              console.log(`🕷️ [Resume] Found active crawl job on mount: ${crawlJob.jobId} (${crawlJob.status})`);
              saveCrawlJobId(crawlJob.jobId);
              setState(prev => ({
                ...prev,
                isAnalyzing: true,
                crawlPhase: {
                  jobId: crawlJob.jobId,
                  status: crawlJob.status as CrawlPhase['status'],
                  postsFound: crawlJob.postsFound,
                  postsExtracted: crawlJob.postsExtracted,
                  error: crawlJob.error,
                },
              }));

              // Resume polling in background — when done, trigger phase 2
              pollCrawlJob(crawlJob.jobId)
                .then(async (crawlDoneStatus) => {
                  console.log('🕷️ [Resume] Crawl complete');
                  setState(prev => ({
                    ...prev,
                    crawlPhase: prev.crawlPhase ? { ...prev.crawlPhase, status: 'done' } : null,
                    htmlPostSelectionSummary: crawlDoneStatus.totalPosts !== undefined ? {
                      totalPosts: crawlDoneStatus.totalPosts,
                      categories: crawlDoneStatus.categories || [],
                      savedSelection: crawlDoneStatus.savedSelection || { maxPosts: null, categories: null, configured: false },
                    } : prev.htmlPostSelectionSummary,
                  }));

                  if (
                    crawlDoneStatus.totalPosts !== undefined &&
                    crawlDoneStatus.totalPosts > POST_SELECTION_THRESHOLD &&
                    !crawlDoneStatus.savedSelection?.configured
                  ) {
                    console.log(`🗂️ [Resume] ${crawlDoneStatus.totalPosts} posts found — gating on post selection`);
                    setState(prev => ({
                      ...prev,
                      isAnalyzing: false,
                      postSelectionGate: {
                        crawlJobId: crawlJob.jobId,
                        isFullBaseline: true,
                        totalPosts: crawlDoneStatus.totalPosts!,
                        categories: crawlDoneStatus.categories || [],
                        savedSelection: crawlDoneStatus.savedSelection || { maxPosts: null, categories: null, configured: false },
                      },
                    }));
                    return;
                  }

                  console.log('🕷️ [Resume] Proceeding to BKA analysis');
                  if (!credentialsRef.current) await fetchCredentials();
                  const phase2Response = await analysisApi.start(siteId, { forceBaseline: true, crawlJobId: crawlJob.jobId });
                  if (phase2Response.sessionId) {
                    await runBkaAnalysis(phase2Response);
                  } else if (phase2Response.alreadyRunning) {
                    console.log('⏳ [Resume] Another analysis is already running — reconnecting to it...');
                    clearCrawlJobId();
                    setState(prev => ({ ...prev, isAnalyzing: false, crawlPhase: null }));
                    await fetchInitialData();
                  } else {
                    // Surface the failure instead of silently leaving isAnalyzing
                    // true forever (e.g. "Crawl found 0 posts..." — see analysis.js).
                    throw new Error(phase2Response.error || 'Failed to start BKA analysis after crawl');
                  }
                })
                .catch((err: Error) => {
                  console.error('🕷️ [Resume] Crawl failed:', err.message);
                  clearCrawlJobId();
                  setState(prev => ({
                    ...prev,
                    isAnalyzing: false,
                    crawlPhase: prev.crawlPhase ? { ...prev.crawlPhase, status: 'error', error: err.message } : null,
                  }));
                });

            } else if (isDone && !analysisAlreadyStarted) {
              // Crawl finished but analysis never started — show "ready" state
              console.log(`🕷️ [Resume] Crawl done on mount, analysis not yet started: ${crawlJob.jobId}`);
              saveCrawlJobId(crawlJob.jobId);
              setState(prev => ({
                ...prev,
                crawlPhase: {
                  jobId: crawlJob.jobId,
                  status: 'done',
                  postsFound: crawlJob.postsFound,
                  postsExtracted: crawlJob.postsExtracted,
                  error: null,
                },
                htmlPostSelectionSummary: crawlJob.totalPosts !== undefined ? {
                  totalPosts: crawlJob.totalPosts,
                  categories: crawlJob.categories || [],
                  savedSelection: crawlJob.savedSelection || { maxPosts: null, categories: null, configured: false },
                } : prev.htmlPostSelectionSummary,
              }));
            } else {
              // Job already handled — clean up
              clearCrawlJobId();
            }
          }
        } catch (crawlErr) {
          // Non-fatal — just means we can't resume crawl state
          console.warn('[Resume] Could not check crawl job state:', crawlErr);
        }
      }

    } catch (err) {
      console.error('Failed to fetch analysis data:', err);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to load analysis data',
      }));
    }
  }, [siteId, portalId, fetchCredentials, connectToSSE, handleAnalysisComplete, crawlJobStorageKey, pollCrawlJob, saveCrawlJobId, clearCrawlJobId, runBkaAnalysis]);

  // Fetch data on mount or site change
  useEffect(() => {
    fetchInitialData();
    return () => closeSSE();
  }, [fetchInitialData, closeSSE]);

  /**
   * Select/deselect tags
   */
  const toggleTag = useCallback((tagId: string) => {
    setState(prev => ({
      ...prev,
      selectedTagIds: prev.selectedTagIds.includes(tagId)
        ? prev.selectedTagIds.filter(id => id !== tagId)
        : [...prev.selectedTagIds, tagId],
    }));
  }, []);

  const selectAllTags = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedTagIds: prev.tags.map(t => t.id),
    }));
  }, []);

  const clearTagSelection = useCallback(() => {
    setState(prev => ({ ...prev, selectedTagIds: [] }));
  }, []);

  /**
   * Skip Phase 1 (crawl) and go straight to Phase 2 (BKA analysis) using an
   * already-completed crawl job.  Called when the crawl finished in a previous
   * session but the analysis never started (orphaned crawl edge case).
   */
  const continueWithAnalysis = useCallback(async (crawlJobId: string) => {
    if (!siteId) return;

    setState(prev => ({ ...prev, error: null, isAnalyzing: true }));

    try {
      // Re-check the crawl job's status fresh — it already carries
      // totalPosts/categories/savedSelection now that it's done, and this
      // path (orphaned-crawl resume) never went through the gate check yet.
      const status = await crawlJobsApi.getStatus(crawlJobId);
      if (status.totalPosts !== undefined) {
        setState(prev => ({
          ...prev,
          htmlPostSelectionSummary: {
            totalPosts: status.totalPosts!,
            categories: status.categories || [],
            savedSelection: status.savedSelection || { maxPosts: null, categories: null, configured: false },
          },
        }));
      }
      if (
        status.totalPosts !== undefined &&
        status.totalPosts > POST_SELECTION_THRESHOLD &&
        !status.savedSelection?.configured
      ) {
        setState(prev => ({
          ...prev,
          isAnalyzing: false,
          postSelectionGate: {
            crawlJobId,
            isFullBaseline: true,
            totalPosts: status.totalPosts!,
            categories: status.categories || [],
            savedSelection: status.savedSelection || { maxPosts: null, categories: null, configured: false },
          },
        }));
        return;
      }

      if (!credentialsRef.current) await fetchCredentials();

      const phase2Response = await analysisApi.start(siteId, {
        forceBaseline: true,
        crawlJobId,
      });

      if (!phase2Response.success || !phase2Response.sessionId) {
        const msg = phase2Response.error || 'Failed to start analysis after crawl';
        setState(prev => ({ ...prev, isAnalyzing: false, error: msg }));
        return;
      }

      await runBkaAnalysis(phase2Response);
    } catch (err) {
      const error = err as Error;
      closeSSE();
      clearCrawlJobId();
      setState(prev => ({
        ...prev,
        isAnalyzing: false,
        error: error.message || 'Failed to start analysis',
      }));
    }
  }, [siteId, fetchCredentials, runBkaAnalysis, closeSSE, clearCrawlJobId]);

  /**
   * Cancel analysis
   */
  const cancelAnalysis = useCallback(async () => {
    if (!siteId || !state.activeAnalysis?.sessionId) {
      return;
    }

    try {
      closeSSE();
      await analysisApi.cancel(siteId, state.activeAnalysis.sessionId);
      setState(prev => ({
        ...prev,
        isAnalyzing: false,
        isPaused: false,
        pauseReason: null,
        activeAnalysis: null,
      }));
    } catch (err) {
      console.error('Cancel analysis error:', err);
    }
  }, [siteId, state.activeAnalysis?.sessionId, closeSSE]);

  /**
   * Resume a paused analysis after user adds API credits
   */
  const resumeAnalysis = useCallback(async () => {
    if (!siteId || !state.activeAnalysis?.sessionId) {
      console.warn('resumeAnalysis: Missing siteId or sessionId');
      return;
    }

    const sessionId = state.activeAnalysis.sessionId;
    console.log('🔄 Resuming analysis:', sessionId);

    setState(prev => ({ ...prev, error: null, isResuming: true }));

    try {
      const result = await analysisApi.resume(siteId, sessionId);

      if (result.success) {
        console.log('✅ Analysis resumed successfully');
        
        // Clear paused state and reconnect to SSE
        setState(prev => ({
          ...prev,
          isPaused: false,
          pauseReason: null,
          isResuming: false,
          isAnalyzing: true,
          activeAnalysis: prev.activeAnalysis ? {
            ...prev.activeAnalysis,
            status: 'analyzing',
            progressMessage: 'Analysis resumed...',
            errorMessage: undefined,
          } : null,
        }));
        
        // Reconnect to SSE for progress updates
        connectToSSE(sessionId);
      } else {
        console.error('❌ Resume failed:', result.error);
        
        // Check if still paused (billing error, throttling, etc.)
        if (result.stillPaused) {
          // Update pause reason with new error if provided
          const newPauseReason = result.error ? parsePauseReason(result.error) : state.pauseReason;
          setState(prev => ({
            ...prev,
            isResuming: false,
            pauseReason: newPauseReason,
            error: result.message || result.error || 'Unable to resume. Please try again.',
          }));
        } else {
          setState(prev => ({
            ...prev,
            isResuming: false,
            isPaused: false,
            pauseReason: null,
            error: result.error || 'Failed to resume analysis',
          }));
        }
      }
    } catch (err) {
      console.error('❌ Resume error:', err);
      setState(prev => ({
        ...prev,
        isResuming: false,
        error: 'Network error. Please check your connection and try again.',
      }));
    }
  }, [siteId, state.activeAnalysis?.sessionId, connectToSSE]);

  /**
   * Manually fetch results for a specific session or the active analysis
   */
  const fetchResults = useCallback(async (sessionId?: string) => {
    const targetSessionId = sessionId || state.activeAnalysis?.sessionId;
    
    if (!siteId || !targetSessionId) {
      console.warn('fetchResults: Missing siteId or sessionId');
      return;
    }

    console.log('📥 fetchResults called for session:', targetSessionId);
    await handleAnalysisComplete(targetSessionId);
  }, [siteId, state.activeAnalysis?.sessionId, handleAnalysisComplete]);

  /**
   * Clear error / info banners
   */
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null, infoMessage: null }));
  }, []);

  /**
   * Calculate unique English posts count across all selected tags
   * Posts can belong to multiple tags, so we need to deduplicate
   */
  const selectedPostsCount = useMemo(() => {
    const uniquePostIds = new Set<string>();
    
    for (const tagId of state.selectedTagIds) {
      const tag = state.tags.find(t => t.id === tagId);
      if (tag?.englishPostIds) {
        // Use unique post IDs if available
        tag.englishPostIds.forEach(id => uniquePostIds.add(id));
      } else if (tag) {
        // Fallback: estimate based on englishCount (less accurate but better than nothing)
        // This path should rarely be used since the backend now provides englishPostIds
        for (let i = 0; i < (tag.englishCount ?? tag.postCount ?? 0); i++) {
          uniquePostIds.add(`${tagId}_fallback_${i}`);
        }
      }
    }
    
    return uniquePostIds.size;
  }, [state.selectedTagIds, state.tags]);

  /**
   * Edit the site's saved HTML post selection outside of an active analysis
   * run (e.g. from the "Edit Selection" control in the Blog Source panel).
   * Does not start or affect any in-progress analysis — takes effect the
   * next time analysis runs.
   */
  const updateHtmlPostSelection = useCallback(async (
    maxPosts: number | null,
    selectedCategories: string[] | null,
  ) => {
    if (!siteId) return;
    const res = await sitesApi.update(siteId, {
      htmlMaxPosts: maxPosts,
      htmlSelectedCategories: selectedCategories,
    });
    if (res.success && res.site?.htmlPostSelection) {
      setState(prev => ({
        ...prev,
        htmlPostSelectionSummary: prev.htmlPostSelectionSummary ? {
          ...prev.htmlPostSelectionSummary,
          savedSelection: res.site!.htmlPostSelection!,
        } : null,
      }));
    }
    return res;
  }, [siteId]);

  return {
    ...state,
    selectedPostsCount,
    toggleTag,
    selectAllTags,
    clearTagSelection,
    startAnalysis,
    continueWithAnalysis,
    confirmPostSelection,
    cancelPostSelectionGate,
    updateHtmlPostSelection,
    cancelAnalysis,
    resumeAnalysis,
    fetchResults,
    clearError,
    refresh: fetchInitialData,
  };
}

export default useAnalysis;
